import { useState } from 'react';
import { AVATARS, PSEUDO_MAX, type GuestMe, type JoinInfo } from '@karozik/shared';
import { api, cn, Spinner } from '@karozik/ui';

export function EmojiPicker({ value, onChange, large = false }: { value: string; onChange(e: string): void; large?: boolean }) {
  return (
    <div className={cn('grid gap-2', large ? 'grid-cols-8' : 'grid-cols-6')}>
      {AVATARS.map((e) => (
        <button
          key={e}
          type="button"
          onClick={() => onChange(e)}
          aria-pressed={value === e}
          className={cn(
            'flex aspect-square items-center justify-center rounded-2xl transition',
            large ? 'text-4xl' : 'text-2xl',
            value === e ? 'bg-gradient-neon scale-105 shadow-lg shadow-pink/30' : 'bg-surface-2 active:scale-95',
          )}
        >
          {e}
        </button>
      ))}
    </div>
  );
}

export function JoinForm({ token, info, onJoined }: { token: string; info: JoinInfo; onJoined(me: GuestMe): void }) {
  const [pseudo, setPseudo] = useState('');
  const [emoji, setEmoji] = useState<string>(() => AVATARS[Math.floor(Math.random() * AVATARS.length)]!);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onJoined(await api<GuestMe>(`/api/join/${token}`, { method: 'POST', json: { pseudo, emoji } }));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col px-5 pt-10 pb-8">
      <p className="text-sm font-semibold tracking-widest text-pink uppercase">Bienvenue à</p>
      <h1 className="font-display mt-1 text-3xl leading-tight font-bold">{info.event.name}</h1>
      <p className="mt-3 text-ink-dim">
        Ce soir, la playlist, c’est vous : propose tes titres, vote pour ceux des autres, mets le feu au dancefloor 🔥
      </p>

      <form onSubmit={submit} className="mt-8 flex flex-1 flex-col gap-6">
        <label className="block">
          <span className="mb-2 block text-sm font-semibold text-ink-dim">Ton pseudo</span>
          <input
            value={pseudo}
            onChange={(e) => setPseudo(e.target.value)}
            maxLength={PSEUDO_MAX}
            autoComplete="nickname"
            placeholder="Tonton Jacques, Mamie Rock…"
            className="w-full rounded-2xl border border-line bg-surface px-4 py-4 text-lg outline-none focus:border-pink"
          />
        </label>
        <div>
          <span className="mb-2 block text-sm font-semibold text-ink-dim">Ton avatar</span>
          <EmojiPicker value={emoji} onChange={setEmoji} />
        </div>
        {error && <p className="rounded-xl bg-danger/15 px-4 py-3 text-sm text-danger">{error}</p>}
        <div className="mt-auto">
          <button
            disabled={pseudo.trim().length < 2 || busy}
            className="bg-gradient-neon flex w-full items-center justify-center gap-2 rounded-2xl py-4 text-lg font-bold shadow-xl shadow-pink/25 transition disabled:opacity-40"
          >
            {busy ? <Spinner /> : <>C’est parti {emoji}</>}
          </button>
          <p className="mt-3 text-center text-xs text-ink-faint">Pas de compte, pas d’e-mail : juste un pseudo, pour ce soir.</p>
        </div>
      </form>
    </main>
  );
}
